// @flow
import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { api } from 'app/api';
import { GET_CAFETERIA_CUSTOMER_ORDERS_SALES, convertParams, convertResult } from 'app/common/api/requests/cafeteria/customer/orders/sales.get';
import type { OrderRequestSale } from 'app/common/components/Cafeteria/dataTypes/orderRequestSales/OrderRequestSale.types';


const PAUSE_BEFORE_LOAD = 300;

const pauseBeforeLoad = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

type Options = {
  customerId: string,
  orderRequestId: string,
}

export default function useProductListDelayed({ customerId, orderRequestId }: Options): [
  Array<OrderRequestSale>,
  boolean
] {
  const { t } = useTranslation('cafeteria');
  const [productList, setProductList] = useState<Array<OrderRequestSale>>([]);

  const request = useCallback(async (orderId: string) => {
    const res = await api.request(GET_CAFETERIA_CUSTOMER_ORDERS_SALES, {
      error: t(
        'order_request_sales_fetch_error',
        'Не удалось получить подробные данные заявки',
      ),
      params: {
        customerId,
        orderId,
      },
      convertions: {
        convertParams,
        convertResult,
      },
    });

    if (res) {
      setProductList(res);
    }
  }, [customerId, t]);


  const [loading, setLoading] = useState(true);
  const [readyToLoad, setReadyToLoad] = useState<string | null>(null);


  useEffect(() => {
    const prepare = async () => {
      setLoading(true);
      // пауза перед запросом данных, так как развернутая строка таблицы
      // может быстро быть переключена при управлении с клавиатуры
      await pauseBeforeLoad(PAUSE_BEFORE_LOAD);
      setReadyToLoad(orderRequestId);
    };
    prepare();
  }, [orderRequestId]);

  useEffect(() => {
    const finalize = async () => {
      if (readyToLoad && readyToLoad === orderRequestId) {
        await request(orderRequestId);
        setLoading(false);
      }
    };
    finalize();
  }, [readyToLoad]); // запрос только при смене orderRequestId, если изменится customerId, то заявка с тем же идентификатором не будет найдена


  return [productList, loading];
}
